import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import type { FixtureTranscriptItem } from '../api/evalApi';

type Props = {
  disabled?: boolean;
  onAddTranscripts: (items: FixtureTranscriptItem[]) => void;
  onError: (message: string) => void;
};

export function inferTranscriptId(filename: string): string {
  return filename
    .replace(/\.[^.]+$/, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function isSupportedTranscriptFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.md') || name.endsWith('.txt');
}

async function filesToTranscripts(files: FileList | File[]): Promise<FixtureTranscriptItem[]> {
  const out: FixtureTranscriptItem[] = [];
  for (const file of Array.from(files)) {
    if (!isSupportedTranscriptFile(file)) {
      throw new Error(
        `Unsupported file type for "${file.name}". Only .md and .txt files are supported.`
      );
    }
    const text = await file.text();
    const id = inferTranscriptId(file.name);
    const isMarkdown = file.name.toLowerCase().endsWith('.md');
    out.push({
      id: id || `transcript-${out.length + 1}`,
      title: file.name.replace(/\.[^.]+$/, ''),
      mime_type: isMarkdown ? 'application/vnd.google-apps.document' : 'text/plain',
      markdown: isMarkdown ? text : undefined,
      content: isMarkdown ? undefined : text,
    });
  }
  return out;
}

export default function EvalFixtureTranscriptDropzone({
  disabled,
  onAddTranscripts,
  onError,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFiles = async (files: FileList | File[]) => {
    try {
      const items = await filesToTranscripts(files);
      onAddTranscripts(items);
    } catch (error: any) {
      onError(error?.message ?? 'Failed to read transcript files.');
    }
  };

  return (
    <Paper
      variant="outlined"
      onDragEnter={(event) => {
        event.preventDefault();
        if (!disabled) setDragActive(true);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setDragActive(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        if (!disabled) setDragActive(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragActive(false);
        if (disabled) {
          return;
        }
        void handleFiles(event.dataTransfer.files);
      }}
      sx={{
        p: 2,
        borderStyle: 'dashed',
        borderColor: dragActive ? 'primary.main' : 'divider',
        bgcolor: dragActive ? 'action.hover' : 'background.paper',
      }}
    >
      <Stack spacing={1.5} alignItems="center">
        <UploadFileIcon color={dragActive ? 'primary' : 'action'} />
        <Box textAlign="center">
          <Typography variant="subtitle2">Drop anonymized transcript files here</Typography>
          <Typography variant="body2" color="text.secondary">
            Supports `.md` and `.txt` files. Markdown files stay as markdown; text files are stored
            as plain content.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          startIcon={<UploadFileIcon />}
        >
          Upload Files
        </Button>
      </Stack>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".md,.txt,text/plain,text/markdown"
        hidden
        onChange={(event) => {
          if (!event.target.files?.length) {
            return;
          }
          void handleFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </Paper>
  );
}
